import axios, { AxiosResponse } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  CreateCoralPayload,
  CreateCoralBatchPayload,
  CreateCoralBatchResponse,
  CoralResponse,
  ListCoralsResponse,
  CoralLogsResponse,
  ReportResponse,
  ListExportsResponse,
  ExportResponse,
  CreateCoralLogPayload,
  UpdateCoralLogPayload,
  CoralChildrenResponse,
  RfidRemovalBatchResponse,
  RfidRemovalRequestItem,
} from '../types';

const API_URL = 'https://ccode.monsoonaquatics.com.au/v1';

interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  message?: string;
}

// Get Authorization header with Cognito JWT token
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    console.log('Fetched auth session:', session);
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      throw new Error('No authentication token available');
    }

    return {
      Authorization: token,
      'Content-Type': 'application/json',
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    throw new Error('Authentication required');
  }
};

export const getData = async (): Promise<ApiResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse = await axios.get(`${API_URL}/corals`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

export const createCoral = async (payload: CreateCoralPayload): Promise<CoralResponse> => {
  try {
    console.log('API createCoral called with payload:', JSON.stringify(payload, null, 2));
    const headers = await getAuthHeaders();
    console.log('Auth headers:', headers);

    const response: AxiosResponse<CoralResponse> = await axios.post(`${API_URL}/corals`, payload, {
      headers,
    });
    console.log('API response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating coral:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 409) {
      throw new Error('RFID already exists in the system');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      console.error('400 error details:', errorData);

      // Pass through the detailed error message from the backend
      let errorMessage = 'Invalid request data';
      if (errorData?.errors) {
        if (typeof errorData.errors === 'string') {
          errorMessage = errorData.errors;
        } else if (Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join('; ');
        }
      }

      throw new Error(errorMessage);
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to create coral record');
  }
};

export const createCoralBatch = async (
  payload: CreateCoralBatchPayload
): Promise<CreateCoralBatchResponse> => {
  try {
    console.log('API createCoralBatch called with payload:', JSON.stringify(payload, null, 2));
    const headers = await getAuthHeaders();

    const response: AxiosResponse<CreateCoralBatchResponse> = await axios.post(
      `${API_URL}/corals`,
      payload,
      {
        headers,
      }
    );
    console.log('API batch response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating coral batch:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 409) {
      throw new Error('Some RFIDs already exist in the system');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      console.error('400 error details:', errorData);

      // Pass through the detailed error message from the backend
      let errorMessage = 'Invalid request data';
      if (errorData?.errors) {
        if (typeof errorData.errors === 'string') {
          errorMessage = errorData.errors;
        } else if (Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join('; ');
        }
      }

      throw new Error(errorMessage);
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to create coral batch');
  }
};

export const createChildFragments = async (
  parentCoralId: string,
  payload: CreateCoralBatchPayload
): Promise<CreateCoralBatchResponse> => {
  try {
    if (!parentCoralId) {
      throw new Error('Parent coral ID is required');
    }

    const headers = await getAuthHeaders();
    const encodedParentId = encodeURIComponent(parentCoralId);
    const response: AxiosResponse<CreateCoralBatchResponse> = await axios.post(
      `${API_URL}/corals/${encodedParentId}/fragments`,
      payload,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error creating child fragments:', error);
    const apiErrors = error.response?.data?.errors;
    if (typeof apiErrors === 'string') {
      throw new Error(apiErrors);
    }
    if (Array.isArray(apiErrors)) {
      throw new Error(apiErrors.join('; '));
    }
    if (error instanceof Error && error.message === 'Parent coral ID is required') {
      throw error;
    }
    throw new Error('Failed to create child fragments');
  }
};

export const listCorals = async (
  limit: number = 200,
  cursor?: string,
  status?: 'ALIVE' | 'DECEASED' | 'EXPORTED',
  filters?: { speciesCode?: string; rfidAttached?: boolean }
): Promise<ListCoralsResponse> => {
  try {
    // console.log('API listCorals called with limit:', limit, 'cursor:', cursor, 'status:', status);
    const headers = await getAuthHeaders();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }
    if (status) {
      params.append('status', status);
    }
    if (filters?.speciesCode?.trim()) {
      params.append('speciesCode', filters.speciesCode.trim());
    }
    if (filters?.rfidAttached !== undefined) {
      params.append('rfidAttached', String(filters.rfidAttached));
    }

    const url = `${API_URL}/corals?${params.toString()}`;
    // console.log('Fetching corals from:', url);

    const response: AxiosResponse<ListCoralsResponse> = await axios.get(url, { headers });
    console.log('API listCorals response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error listing corals:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to fetch coral records');
  }
};

export const getCoral = async (coralId: string): Promise<CoralResponse> => {
  try {
    if (!coralId) {
      throw new Error('Missing coralId');
    }

    const headers = await getAuthHeaders();
    const encodedId = encodeURIComponent(coralId);
    const response: AxiosResponse<CoralResponse> = await axios.get(
      `${API_URL}/corals/${encodedId}`,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching coral by ID:', error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404 || error.response?.status === 406) {
      throw new Error('Coral not found');
    }

    const errorData = error.response?.data;
    if (errorData?.errors) {
      throw new Error(errorData.errors);
    }

    throw new Error('Failed to fetch coral');
  }
};

export const getCoralChildren = async (
  coralId: string,
  limit: number = 50,
  cursor?: string
): Promise<CoralChildrenResponse> => {
  try {
    if (!coralId) {
      throw new Error('Coral ID is required');
    }

    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) {
      params.append('cursor', cursor);
    }

    const encodedId = encodeURIComponent(coralId);
    const response: AxiosResponse<CoralChildrenResponse> = await axios.get(
      `${API_URL}/corals/${encodedId}/children?${params.toString()}`,
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching child fragments:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    }
    if (error.response?.status === 403) {
      throw new Error('Access denied');
    }
    if (error.response?.status === 404 || error.response?.status === 406) {
      throw new Error('Coral not found');
    }
    const apiErrors = error.response?.data?.errors;
    if (typeof apiErrors === 'string') {
      throw new Error(apiErrors);
    }
    throw new Error('Failed to fetch child fragments');
  }
};

export const updateCoral = async (
  coralId: string,
  payload: Partial<CreateCoralPayload>
): Promise<CoralResponse | null> => {
  try {
    console.log(
      'API updateCoral called with coralId:',
      coralId,
      'payload:',
      JSON.stringify(payload, null, 2)
    );
    const headers = await getAuthHeaders();

    const url = `${API_URL}/corals/${coralId}`;
    // console.log('Updating coral at:', url);

    const response: AxiosResponse<{ message: string; coral: CoralResponse }> = await axios.put(
      url,
      payload,
      { headers }
    );
    console.log('API updateCoral response:', response.data);
    return response.data.coral;
  } catch (error: any) {
    console.error('Error updating coral:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Coral not found');
    } else if (error.response?.status === 406) {
      throw new Error('Coral not found');
    } else if (error.response?.status === 409) {
      throw new Error('RFID already exists in the system');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Invalid data provided');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to update coral record');
  }
};

export const removeCoralRfids = async (
  items: RfidRemovalRequestItem[],
  reason: string
): Promise<RfidRemovalBatchResponse> => {
  try {
    const headers = await getAuthHeaders();
    const response: AxiosResponse<RfidRemovalBatchResponse> = await axios.post(
      `${API_URL}/corals/rfid-removals`,
      { items, reason },
      { headers }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error removing coral RFIDs:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    }
    if (error.response?.status === 403) {
      throw new Error('Access denied');
    }
    if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }
    throw new Error('Failed to remove RFID tags');
  }
};

// Returns a CoralResponse if found, null if not found
export const getCoralByRfid = async (rfid: string): Promise<CoralResponse | null> => {
  try {
    console.log('API getCoralByRfid called with rfid:', rfid);
    const headers = await getAuthHeaders();

    const encodedRfid = encodeURIComponent(rfid.trim());
    const url = `${API_URL}/corals/rfid/${encodedRfid}`;

    const response: AxiosResponse<any> = await axios.get(url, {
      headers,
    });
    console.log('API getCoralByRfid response data:', response.data);

    // Check if we got HTML instead of JSON (indicates wrong endpoint or 404)
    if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
      console.error(
        'Received HTML response instead of JSON - likely 403 unauthorized or 404, wrong endpoint.'
      );
      throw new Error(
        'Error: Received HTML response from API request (likely CloudFront error page)'
      );
    }

    // Handle AWS Lambda proxy response format
    if (response.data && typeof response.data === 'object' && 'statusCode' in response.data) {
      console.log('Detected AWS Lambda proxy response format');
      const lambdaStatusCode = response.data.statusCode;

      if (lambdaStatusCode >= 400) {
        // Try to parse the error from the body
        let errorMessage = 'API error';
        if (response.data.body) {
          try {
            const errorBody = JSON.parse(response.data.body);
            errorMessage = errorBody.errors || errorBody.message || errorMessage;
          } catch (parseError) {
            console.error('Failed to parse error body:', parseError);
            errorMessage = response.data.body;
          }
        }
        throw new Error(errorMessage);
      }

      // For successful responses, parse the body
      if (response.data.body) {
        try {
          return JSON.parse(response.data.body);
        } catch (parseError) {
          console.error('Failed to parse response body:', parseError);
          throw new Error('Invalid response format');
        }
      }
    }

    // Return the data directly if it's not in Lambda proxy format
    return response.data;
  } catch (error: any) {
    // TODO: Backend returns 406 if no coral found with that RFID
    if (error.response?.status === 406) {
      console.log('No coral found');
      return null;
    }

    console.error('Error getting coral by RFID:', error);
    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Not found');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    // If we already threw a specific error above, re-throw it
    if (error.message === 'Coral not found') {
      throw error;
    }

    throw new Error('Failed to get coral record');
  }
};

export const getCoralLogs = async (
  coralId: string,
  limit: number = 25,
  cursor?: string
): Promise<CoralLogsResponse> => {
  const normalizePayload = (payload: any): CoralLogsResponse => {
    if (!payload) {
      return { items: [] };
    }

    if (Array.isArray(payload.items) || payload.items === undefined) {
      return {
        items: payload.items ?? [],
        nextCursor: payload.nextCursor,
      };
    }

    if (typeof payload.body === 'string') {
      try {
        const parsed = JSON.parse(payload.body);
        return normalizePayload(parsed);
      } catch (parseErr) {
        console.error('Failed to parse logs payload body', parseErr);
      }
    }

    return { items: [] };
  };

  try {
    if (!coralId) {
      throw new Error('Missing coralId');
    }

    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (limit) {
      params.append('limit', limit.toString());
    }
    if (cursor) {
      params.append('cursor', cursor);
    }

    const encodedId = encodeURIComponent(coralId);
    const query = params.toString();
    const url = `${API_URL}/corals/${encodedId}/logs${query ? `?${query}` : ''}`;
    console.log('Fetching coral logs from URL: ', url);
    const response: AxiosResponse<CoralLogsResponse | { body: string }> = await axios.get(url, {
      headers,
    });
    console.log('API getCoralLogs response:', response.data);
    return normalizePayload(response.data);
  } catch (error: any) {
    console.error('Error fetching coral logs:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404 || error.response?.status === 406) {
      throw new Error('Coral logs not found');
    }

    const errorData = error.response?.data;
    if (errorData?.errors) {
      throw new Error(errorData.errors);
    }

    throw new Error('Failed to fetch coral logs');
  }
};

export const createCoralLog = async (
  coralId: string,
  payload: CreateCoralLogPayload
): Promise<CoralLogsResponse> => {
  try {
    if (!coralId) {
      throw new Error('Coral ID is required to create a log');
    }

    const headers = await getAuthHeaders();
    console.log(`Creating coral log for coralId: ${coralId} with headers:`, headers);
    const encodedId = encodeURIComponent(coralId);
    const url = `${API_URL}/corals/${encodedId}/logs`;
    console.log('Creating coral log at URL:', url, 'with payload:', payload);
    const response: AxiosResponse<CoralLogsResponse> = await axios.post(url, payload, {
      headers,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error creating coral log:', error);
    const apiErrors = error.response?.data?.errors;
    if (typeof apiErrors === 'string') {
      throw new Error(apiErrors);
    }
    if (Array.isArray(apiErrors)) {
      throw new Error(apiErrors.join(', '));
    }
    throw new Error('Failed to create tracking log');
  }
};

export const updateCoralLog = async (
  coralId: string,
  logId: string,
  payload: UpdateCoralLogPayload
): Promise<CoralLogsResponse> => {
  try {
    if (!coralId) {
      throw new Error('Coral ID is required to update a log');
    }
    if (!logId) {
      throw new Error('Log ID is required to update a log');
    }

    const headers = await getAuthHeaders();
    const encodedCoralId = encodeURIComponent(coralId);
    const encodedLogId = encodeURIComponent(logId);
    const url = `${API_URL}/corals/${encodedCoralId}/logs/${encodedLogId}`;
    const response: AxiosResponse<CoralLogsResponse> = await axios.patch(url, payload, {
      headers,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating coral log:', error);
    const apiErrors = error.response?.data?.errors;
    if (typeof apiErrors === 'string') {
      throw new Error(apiErrors);
    }
    if (Array.isArray(apiErrors)) {
      throw new Error(apiErrors.join(', '));
    }
    throw new Error('Failed to update tracking log');
  }
};

export const deleteCoralLog = async (coralId: string, logId: string): Promise<void> => {
  try {
    if (!coralId) {
      throw new Error('Coral ID is required to delete a log');
    }
    if (!logId) {
      throw new Error('Log ID is required to delete a log');
    }

    const headers = await getAuthHeaders();
    const encodedCoralId = encodeURIComponent(coralId);
    const encodedLogId = encodeURIComponent(logId);
    const url = `${API_URL}/corals/${encodedCoralId}/logs/${encodedLogId}`;
    await axios.delete(url, { headers });
  } catch (error: any) {
    console.error('Error deleting coral log:', error);
    const apiErrors = error.response?.data?.errors;
    if (typeof apiErrors === 'string') {
      throw new Error(apiErrors);
    }
    if (Array.isArray(apiErrors)) {
      throw new Error(apiErrors.join(', '));
    }
    throw new Error('Failed to delete tracking log');
  }
};

export const deleteCoral = async (coralId: string): Promise<void> => {
  try {
    console.log('API deleteCoral called with coralId:', coralId);
    const headers = await getAuthHeaders();

    const url = `${API_URL}/corals/${coralId}`;
    console.log('Deleting coral at:', url);

    const response: AxiosResponse = await axios.delete(url, { headers });
    console.log('API deleteCoral response:', response.data);
  } catch (error: any) {
    console.error('Error deleting coral:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Coral not found');
    } else if (error.response?.status === 406) {
      throw new Error('Coral not found');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to delete coral record');
  }
};

export interface DashboardData {
  cumulatives: {
    byDay: Array<{
      date: string;
      alive: number;
      deceased: number;
      exported: number;
      total: number;
    }>;
  };
  species_count: {
    top: Array<{
      speciesCode: string;
      count: number;
      pct: number;
    }>;
    total_species: number;
  };
  recent_entries: Array<{
    coralId: string;
    speciesCode: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }>;
  kpis: {
    survival_rate: number;
    mortality_rate: number;
    export_rate: number;
    species_diversity: number;
  };
}

export const getDashboardData = async (
  dashboardId: string = 'home',
  dateFrom?: string,
  dateTo?: string
): Promise<DashboardData> => {
  try {
    console.log('API getDashboardData called with:', { dashboardId, dateFrom, dateTo });
    const headers = await getAuthHeaders();

    // Build query parameters
    const params = new URLSearchParams();
    if (dateFrom) {
      params.append('dateFrom', dateFrom);
    }
    if (dateTo) {
      params.append('dateTo', dateTo);
    }

    const url = `${API_URL}/corals/analytics/dashboard/${dashboardId}${params.toString() ? `?${params.toString()}` : ''}`;
    console.log('Fetching dashboard data from:', url);

    const response: AxiosResponse<DashboardData> = await axios.get(url, { headers });
    console.log('API getDashboardData response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching dashboard data:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Dashboard not found');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to fetch dashboard data');
  }
};

export const generateReport = async (
  reportType: string,
  dateFrom: string,
  dateTo: string
): Promise<ReportResponse> => {
  try {
    console.log('API generateReport called with:', { reportType, dateFrom, dateTo });
    const headers = await getAuthHeaders();

    const payload = {
      reportType,
      dateFrom,
      dateTo,
    };

    const response: AxiosResponse<ReportResponse> = await axios.post(
      `${API_URL}/reports`,
      payload,
      { headers }
    );
    console.log('API generateReport response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error generating report:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Invalid report parameters');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to generate report');
  }
};

// Get list of reports
export const getReports = async (
  reportType?: string,
  cursor?: string,
  limit?: number,
  dateFrom?: string,
  dateTo?: string
): Promise<{
  items: {
    reportId: string;
    reportType: string;
    dateFrom: string;
    dateTo: string;
    format: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    createdDate: string;
    generatedAt?: string;
    expiresAt?: string;
    s3Bucket?: string;
    s3Key?: string;
    s3Path?: string;
  }[];
  nextCursor?: string;
}> => {
  try {
    console.log('API getReports called with:', { reportType, cursor, limit, dateFrom, dateTo });
    const headers = await getAuthHeaders();

    // Build query parameters
    const params = new URLSearchParams();
    if (reportType) {
      params.append('reportType', reportType);
    }
    if (cursor) {
      params.append('cursor', cursor);
    }
    if (limit) {
      params.append('limit', limit.toString());
    }
    if (dateFrom) {
      params.append('dateFrom', dateFrom);
    }
    if (dateTo) {
      params.append('dateTo', dateTo);
    }

    const url = `${API_URL}/reports${params.toString() ? '?' + params.toString() : ''}`;
    console.log('Fetching reports from:', url);

    const response = await axios.get(url, { headers });
    console.log('API getReports response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching reports:', error);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    }

    const errorData = error.response?.data;
    if (error.response?.status === 400 && errorData?.errors) {
      throw new Error(errorData.errors);
    }

    throw new Error('Failed to fetch reports');
  }
};

// Get specific report by ID
export const getReportById = async (
  reportId: string
): Promise<{
  reportId: string;
  reportType: string;
  dateFrom: string;
  dateTo: string;
  format: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdDate: string;
  generatedAt?: string;
  expiresAt?: string;
  s3Bucket?: string;
  s3Key?: string;
  s3Path?: string;
}> => {
  try {
    console.log('API getReportById called with:', { reportId });
    const headers = await getAuthHeaders();

    const response = await axios.get(`${API_URL}/reports/${reportId}`, { headers });
    console.log('API getReportById response:', response.data);

    // The API returns { items: [report] } format, so we extract the first item
    if (response.data.items && response.data.items.length > 0) {
      return response.data.items[0];
    }

    throw new Error('Report not found');
  } catch (error: any) {
    console.error('Error fetching report by ID:', error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 406) {
      throw new Error('Report not found');
    }

    const errorData = error.response?.data;
    if (errorData?.errors) {
      throw new Error(errorData.errors);
    }

    throw new Error('Failed to fetch report');
  }
};

// Download report by getting presigned URL
export const downloadReportById = async (
  reportId: string
): Promise<{
  downloadUrl: string;
  method: string;
  expiresIn: number;
  reportId: string;
  s3Bucket: string;
  s3Key: string;
}> => {
  try {
    console.log('API downloadReportById called with:', { reportId });
    const headers = await getAuthHeaders();

    const response = await axios.post(`${API_URL}/reports/${reportId}/download`, {}, { headers });
    console.log('API downloadReportById response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error downloading report:', error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 406) {
      throw new Error('Report not found');
    }

    const errorData = error.response?.data;
    if (errorData?.errors) {
      throw new Error(errorData.errors);
    }

    throw new Error('Failed to get download URL');
  }
};

export const listExports = async (
  limit: number = 200,
  cursor?: string,
  company?: string,
  exportName?: string,
  exportDate?: string
): Promise<ListExportsResponse> => {
  try {
    // console.log('API listExports called with limit:', limit, 'cursor:', cursor);
    const headers = await getAuthHeaders();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (cursor) {
      params.append('cursor', cursor);
    }
    if (company) {
      params.append('company', company);
    }
    if (exportName) {
      params.append('exportName', exportName);
    }
    if (exportDate) {
      params.append('exportDate', exportDate);
    }

    const url = `${API_URL}/exports?${params.toString()}`;
    console.log('Fetching exports from:', url);

    const response: AxiosResponse<ListExportsResponse> = await axios.get(url, { headers });
    console.log('API listExports response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error listing exports:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to fetch export records');
  }
};

export const getExport = async (exportId: string): Promise<ExportResponse> => {
  try {
    console.log('API getExport called with exportId:', exportId);
    const headers = await getAuthHeaders();

    const url = `${API_URL}/exports/${exportId}`;
    console.log('Fetching export from:', url);

    const response: AxiosResponse<ExportResponse> = await axios.get(url, { headers });
    console.log('API getExport response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching export:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Export not found');
    } else if (error.response?.status === 406) {
      throw new Error('Export not found');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to fetch export');
  }
};

export interface CreateExportPayload {
  company: string;
  exportName?: string;
  exportDate: string;
  cites?: string;
  coralIds?: string[]; // Array of coral IDs to include in export
}

export const createExport = async (payload: CreateExportPayload): Promise<any> => {
  try {
    console.log('API createExport called with payload:', JSON.stringify(payload, null, 2));
    const headers = await getAuthHeaders();

    const response: AxiosResponse = await axios.post(`${API_URL}/exports`, payload, {
      headers,
    });
    console.log('API createExport response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating export:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Invalid export data provided');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to create export');
  }
};

export const updateExport = async (
  exportId: string,
  payload: Partial<CreateExportPayload>
): Promise<any> => {
  try {
    console.log(
      'API updateExport called with exportId:',
      exportId,
      'payload:',
      JSON.stringify(payload, null, 2)
    );
    const headers = await getAuthHeaders();

    const url = `${API_URL}/exports/${exportId}`;
    console.log('Updating export at:', url);

    const response: AxiosResponse = await axios.patch(url, payload, { headers });
    console.log('API updateExport response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating export:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Export not found');
    } else if (error.response?.status === 406) {
      throw new Error('Export not found');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Invalid export data provided');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to update export');
  }
};

export const deleteExport = async (exportId: string): Promise<void> => {
  try {
    console.log('API deleteExport called with exportId:', exportId);
    const headers = await getAuthHeaders();

    const url = `${API_URL}/exports/${exportId}`;
    console.log('Deleting export at:', url);

    const response: AxiosResponse = await axios.delete(url, { headers });
    console.log('API deleteExport response:', response.data);
  } catch (error: any) {
    console.error('Error deleting export:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Export not found');
    } else if (error.response?.status === 406) {
      throw new Error('Export not found');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to delete export');
  }
};

export interface CoralAssignmentAction {
  action: 'add' | 'remove';
  corals: string[];
}

export interface ModifyCoralAssignmentsResponse {
  message: string;
  added: string[];
  removed: string[];
}

export const modifyCoralAssignments = async (
  exportId: string,
  actions: CoralAssignmentAction[]
): Promise<ModifyCoralAssignmentsResponse> => {
  try {
    console.log(
      'API modifyCoralAssignments called with exportId:',
      exportId,
      'actions:',
      JSON.stringify(actions, null, 2)
    );
    const headers = await getAuthHeaders();

    const url = `${API_URL}/exports/${exportId}/coral-assignments/modify`;
    console.log('Modifying coral assignments at:', url);

    const response: AxiosResponse<ModifyCoralAssignmentsResponse> = await axios.post(
      url,
      { actions },
      { headers }
    );
    console.log('API modifyCoralAssignments response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error modifying coral assignments:', error);
    console.error('Error response data:', error.response?.data);
    console.error('Error response status:', error.response?.status);

    // Handle specific API errors
    if (error.response?.status === 401) {
      throw new Error('Authentication required');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied');
    } else if (error.response?.status === 404) {
      throw new Error('Export not found');
    } else if (error.response?.status === 406) {
      throw new Error('Export not found');
    } else if (error.response?.status === 400) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Invalid coral assignment data provided');
    } else if (error.response?.status === 409) {
      const errorData = error.response.data;
      throw new Error(errorData?.errors || 'Conflict with existing coral assignments');
    } else if (error.response?.data?.errors) {
      throw new Error(error.response.data.errors);
    }

    throw new Error('Failed to modify coral assignments');
  }
};
